import * as React from 'react';
import * as _ from 'lodash';
import {
  Certificate,
  Contact,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  countryIso2ToIso3,
  DisplayDataStateProvince,
  Footer,
  getCountries,
  getPhonePrefixForCountry,
  getStateProvinces,
  Logging,
  renderEdit,
  renderSelect,
  stateProvinceAbbreviationToObject,
  validatePhone,
  validatePhoneExtension,
  validateEmail,
  validatePostalCode,
  DisplayData,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import ContactInformationProps from './ContactInformationProps.interface';
import '../steps.scss';
import SelectedItems from '../../Common/selectedItems';

const maxEmailLength = 100;
const maxAddressLength = 100;

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: ['name', 'select-contact'],
  emailFields: ['email'],
  phoneFields: ['phone', 'fax'],
  phoneExtensionFields: ['phoneExtension'],
  postalCodeFields: ['postalCode'],
  minMaxSize: [
    { field: 'email', maxSize: maxEmailLength },
    { field: 'address', maxSize: maxAddressLength },
    { field: 'address2', maxSize: maxAddressLength },
  ],
  customMessages: null,
};

const ContactInformation = ({
  gatewayEndPoint,
  step,
  certificate,
  selectedItems,
  onCancel,
  onContinuteSave,
}: ContactInformationProps): JSX.Element => {
  Logging.Info('Contact Information');
  const [selectedContactUuid, setSelectedContactUuid] = React.useState(certificate.contact?.uuid);

  const { contact } = certificate;

  const [addContact, setAddContact] = React.useState(contact && !contact.uuid);

  const [name, setName] = React.useState(contact?.name);
  const [department, setDepartment] = React.useState(contact?.department);
  const [email, setEmail] = React.useState(contact?.email);
  const [phone, setPhone] = React.useState(contact?.phone);
  const [phoneExtension, setPhoneExtension] = React.useState(contact?.phoneExtension);
  const [fax, setFax] = React.useState(contact?.fax);
  const [address, setAddress] = React.useState(contact?.address);
  const [address2, setAddress2] = React.useState(contact?.address2);
  const [city, setCity] = React.useState(contact?.city);
  const [state, setState] = React.useState(contact?.state);
  const [postalCode, setPostalCode] = React.useState(contact?.postalCode);
  const [country, setCountry] = React.useState(countryIso2ToIso3(contact?.country));
  const [phonePrefix, setPhonePrefix] = React.useState(getPhonePrefixForCountry(contact?.country));

  // Get the Certificate Contacts
  const getCertificateContacts = (): DisplayData[] => {
    Logging.Debug('Certificate Contacts', selectedItems?.customer?.contacts);

    const contacts: DisplayData[] = [
      {
        data: 'add',
        display: 'Add a New Contact',
      },
    ];

    // eslint-disable-next-line arrow-body-style
    const customerContacts: DisplayData[] = selectedItems?.customer?.contacts?.map((customerContact: Contact) => {
      return {
        data: customerContact.uuid,
        display: customerContact.name || 'No Contact Name provided',
      };
    });

    return customerContacts ? contacts.concat(customerContacts) : contacts;
  };

  // Country was Changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // Country was Changed.  Check to see if the State is still Valid
    if (country && state) {
      const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(state);

      // Clear the State if a differnt Country was picked
      if (displayDataGeo && (country !== displayDataGeo.country)) {
        setState(null);
      }
    } else if (!country && state) {
      setState(null);
    }

    // Set the Phone Prefix
    setPhonePrefix(getPhonePrefixForCountry(country));
  }, [country]);

  // State was changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // State is Selected but not the Country.
    if (state && !country) {
      const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(state);

      // Picking the Country from the State
      if (displayDataGeo) {
        setCountry(displayDataGeo.country);
      }
    }
  }, [state]);

  // Contact Selection changed
  React.useEffect(() => {
    if (selectedContactUuid === 'add') {
      setName(null);
      setDepartment(null);
      setAddress(null);
      setAddress2(null);
      setCity(null);
      setState(null);
      setPostalCode(null);
      setCountry(null);
      setPhone(null);
      setPhoneExtension(null);
      setFax(null);
      setEmail(null);

      setAddContact(true);
    }
  }, [selectedContactUuid]);

  // Rather Select a Contact
  const onRatherSelect = (): void => {
    setAddContact(false);
    setSelectedContactUuid(null);
  };

  // Continue / Save Button
  const onSaveContinue = (): void => {
    const copyCertificate: Certificate = _.cloneDeep(certificate);
    const copySelectedItems: SelectedItems = _.cloneDeep(selectedItems);

    if (addContact) {
      copyCertificate.contact = {
        uuid: null,
        name,
        department,
        email,
        phone,
        phoneExtension,
        fax,
        address,
        address2,
        city,
        state,
        postalCode,
        country,
        primaryInd: false,
        emailSubscribedInd: true,
      };
    } else {
      const contactObject : Contact = selectedItems?.customer.contacts.filter((contactItem: Contact) => contactItem.uuid === selectedContactUuid)[0];

      copyCertificate.contact = contactObject;
    }

    Logging.Info('Continue to Step 4:', copyCertificate, copySelectedItems);

    onContinuteSave(step, copyCertificate, copySelectedItems);
  };

  // Validate the Step
  const validateStep = (): boolean => {
    // Adding a New Contact
    if (addContact) {
      let validated = true;

      // Validate Name
      if (!name) {
        validated = false;
      }

      // Validate Email
      if (!validateEmail(email)) {
        validated = false;
      }

      // Validate the Email
      if (email && (email.length > maxEmailLength)) {
        validated = false;
      }

      // Validate the Address
      if (address && (address.length > maxAddressLength)) {
        validated = false;
      }

      // Validate the Address 2
      if (address2 && (address2.length > maxAddressLength)) {
        validated = false;
      }

      // Validate Phone
      if (!validatePhone(phone)) {
        validated = false;
      }

      // Validate Fax
      if (!validatePhone(fax)) {
        validated = false;
      }

      // Validate Phone Extension
      if (!validatePhoneExtension(phoneExtension)) {
        validated = false;
      }

      // Postal Zip
      if (!validatePostalCode(postalCode, country)) {
        validated = false;
      }

      return validated;
    }

    // Selecting a Contact
    if (!selectedContactUuid) {
      return false;
    }

    return true;
  };

  const canSaveContinue = validateStep();

  // Set the Country used in Validation
  controlValidation.country = country;

  return (
    <>
      <div className="stepContent">
        {
          addContact
            ? (
              <>
                {renderEdit(50, 'name', 'Contact Name*', 'text', null,
                  name, setName, controlStateEditable, controlStyle, controlValidation, null)}
                {renderEdit(50, 'department', 'Department', 'text', null,
                  department, setDepartment, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(50, 'email', 'E-Mail', 'email', null, email, setEmail, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(50, 'phone', 'Phone Number', 'tel', '(xxx) xxx-xxxx',
                  phone, setPhone, controlStateEditable, controlStyle, controlValidation, phonePrefix)}
                {renderEdit(50, 'phoneExtension', 'Extension', 'text', null,
                  phoneExtension, setPhoneExtension, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(50, 'fax', 'Fax Number', 'tel', '(xxx) xxx-xxxx',
                  fax, setFax, controlStateEditable, controlStyle, controlValidation, phonePrefix)}
                {renderEdit(50, 'address', 'Street Address', 'text', null,
                  address, setAddress, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(50, 'address2', 'Street Address 2', 'text', null,
                  address2, setAddress2, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(50, 'city', 'City', 'text', null, city, setCity, controlStateEditable, controlStyle, controlValidation)}
                {renderSelect(50, 'state', 'State / Province', 'Select State / Province', false, false,
                  getStateProvinces(country), true, null, state, setState, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(50, 'postalCode', 'Postal / Zip Code', 'text', null,
                  postalCode, setPostalCode, controlStateEditable, controlStyle, controlValidation)}
                {renderSelect(50, 'country', 'Country', 'Select Country', false, false,
                  getCountries(), true, null, country, setCountry, controlStateEditable, controlStyle, controlValidation)}
                <div className="buttonRow">
                  <VtxButton
                    id="selectButton"
                    icon={<VtxIcon type="select" />}
                    onClick={onRatherSelect}
                    type="default"
                  >
                    Rather Select a Contact
                  </VtxButton>
                </div>
              </>
            )
            : (
              <>
                {renderSelect(66, 'select-contact', 'Select or Add a Contact', 'Select a Contact', false, true, getCertificateContacts(),
                  true, null, selectedContactUuid, setSelectedContactUuid, controlStateEditable, controlStyle, controlValidation)}
              </>
            )
        }
      </div>
      <Footer onCancel={onCancel} saveEnabled={canSaveContinue} onSave={onSaveContinue} saveText="Continue" />
    </>
  );
};

export default ContactInformation;
